import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Plus } from "lucide-react";
import EngineIcon from "./EngineIcon";
import { speak, cancelSpeech } from "@/lib/speak";

// Hoisted so it isn't redefined on every render (prevents remount/dropped taps).
function ObjectGroup({ n, offset, tint, icon, counted, setCounted }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {Array.from({ length: n }).map((_, i) => {
        const idx = offset + i;
        const on = counted.includes(idx);
        return (
          <motion.button
            key={idx}
            whileTap={{ scale: 0.85 }}
            onClick={() => setCounted((c) => (c.includes(idx) ? c : [...c, idx]))}
            data-testid={`count-object-${idx}`}
            className={`relative flex h-14 w-14 items-center justify-center rounded-2xl border-2 transition-colors ${
              on ? "border-[#10B981] bg-[#10B981]/10" : "border-[#E5DECF] bg-white"
            }`}
            style={{ color: tint }}>
            <EngineIcon name={icon} size={26} />
            {on && <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#10B981] text-[10px] font-bold text-white">{counted.indexOf(idx) + 1}</span>}
          </motion.button>
        );
      })}
    </div>
  );
}

// Reusable math manipulatives engine: count a group, or add two groups of objects.
export default function CountEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const isAdd = cfg.stage === "add";
  const [counted, setCounted] = useState([]);
  const [choice, setChoice] = useState(null);
  const advanced = useRef(false);

  const totalObjects = (cfg.a || 0) + (cfg.b || 0);
  const target = isAdd ? cfg.answer : cfg.target;
  const answered = choice !== null;
  const correct = choice === target;
  const allCounted = counted.length >= totalObjects;

  useEffect(() => {
    setCounted([]); setChoice(null); advanced.current = false;
    if (autoplay && activity.prompt) speak(activity.prompt, "nova", { speed: 0.9 });
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, autoplay]);

  useEffect(() => {
    if (autoplay && allCounted && !answered) speak(isAdd ? "How many in all?" : "How many did you count?", "nova", { speed: 0.9 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCounted]);

  useEffect(() => {
    if (autoplay && answered && correct && !advanced.current) {
      advanced.current = true;
      speak("Correct!", "nova", { speed: 0.95 });
      const t = setTimeout(() => onComplete(100), 1500);
      return () => clearTimeout(t);
    }
  }, [autoplay, answered, correct, onComplete]);

  return (
    <div className="text-center" data-testid="count-engine">
      <p className="font-body text-base sm:text-lg text-[#6B7280]">{activity.prompt}</p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
        <div className="rounded-3xl bg-[#FAF7F2] p-5">
          <ObjectGroup n={cfg.a || 0} offset={0} tint="#E05A47" icon={cfg.icon} counted={counted} setCounted={setCounted} />
          <div className="mt-2 font-heading text-lg font-bold text-[#E05A47]">{cfg.a}</div>
        </div>
        {isAdd && (
          <>
            <Plus size={32} className="text-[#6B7280]" />
            <div className="rounded-3xl bg-[#FAF7F2] p-5">
              <ObjectGroup n={cfg.b || 0} offset={cfg.a || 0} tint="#0284C7" icon={cfg.icon} counted={counted} setCounted={setCounted} />
              <div className="mt-2 font-heading text-lg font-bold text-[#0284C7]">{cfg.b}</div>
            </div>
          </>
        )}
      </div>

      {allCounted && (
        <div className="mt-8">
          <p className="font-heading text-xl font-bold text-[#1F2937]">
            {isAdd ? "How many in all?" : "How many did you count?"}
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-3">
            {(cfg.options || []).map((opt, i) => {
              const chosen = choice === opt;
              const showState = answered && (opt === target || chosen);
              const good = opt === target;
              return (
                <button
                  key={i}
                  disabled={answered}
                  onClick={() => setChoice(opt)}
                  data-testid={`count-answer-${opt}`}
                  className={`h-16 w-16 rounded-2xl border-2 font-heading text-2xl font-extrabold transition-colors ${
                    showState
                      ? good ? "border-[#10B981] bg-[#10B981]/10 text-[#047857]" : "border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]"
                      : "border-[#E5DECF] bg-white text-[#1F2937] hover:border-[#E05A47]"
                  }`}>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        {!allCounted && <p className="font-body text-sm text-[#94A3B8]">Tap every object to count it.</p>}
        {answered && correct && (
          <button onClick={() => onComplete(100)} data-testid="count-continue"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
            <CheckCircle2 size={18} /> {isAdd ? `${cfg.a} + ${cfg.b} = ${cfg.answer}!` : "Correct!"} Next <ArrowRight size={18} />
          </button>
        )}
        {answered && !correct && (
          <button onClick={() => setChoice(null)} data-testid="count-retry" className="rounded-full bg-[#F59E0B] px-8 py-3 font-heading font-bold text-white">Count again</button>
        )}
      </div>
    </div>
  );
}
